import React, { useCallback, useEffect, useState } from 'react'
import Wrapper from '../../components/Wrapper'
import { global, short, shortFunction, shortFunctionList } from '../../translate'
import { useNavigate, useLocation } from 'react-router-dom'
import { Box, Typography, Paper, TextField, Button, IconButton, CircularProgress, Grid, Alert, FormControlLabel, Switch, ToggleButton, ToggleButtonGroup } from '@mui/material'
import { encodeFamiliarDuration, decodeFamiliarDuration } from '../../helpers/familiarDuration'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import InfoIcon from '@mui/icons-material/Info'
import Api from '../../Api'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { greenTheme, sectionTitleStyles } from '../../helpers/style'

const { REACT_APP_SERVER } = process.env
const primaryColor = REACT_APP_SERVER === 'ATS' ? '#0b73b9' : greenTheme.primary
const hoverColor = REACT_APP_SERVER === 'ATS' ? '#095a8f' : greenTheme.hover
const inputStylesLocal = {
    '& .MuiOutlinedInput-root': { minHeight: '41px', '& fieldset': { borderColor: primaryColor }, '&:hover fieldset': { borderColor: hoverColor }, '&.Mui-focused fieldset': { borderColor: primaryColor } },
    '& .MuiInputLabel-root.Mui-focused': { color: primaryColor },
}

const CODE_VOICEMAIL = 100097
const CODE_FAMILIAR = 10999
const CODE_ENDING = 10888

export default function VoiceMailFamiliar() {
    const langIndex = +localStorage.getItem('atsLang') || 1
    const navigate = useNavigate()
    const location = useLocation()
    const section = location.state?.section
    const userData = useSelector((state) => state.users.userData)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [shortList, setShortList] = useState([])

    const voicemailRow = shortList.find((s) => +s.function_code === CODE_VOICEMAIL)
    const familiarRow = shortList.find((s) => +s.function_code === CODE_FAMILIAR)
    const endingRow = shortList.find((s) => +s.function_code === CODE_ENDING)

    const [voicemail, setVoicemail] = useState({ day_count: 3, status: 0 })
    const [familiar, setFamiliar] = useState({ durationValue: 3, durationUnit: 'days', status: 0, in_type: 0, out_type: 0 })

    useEffect(() => {
        (async () => {
            setLoading(true)
            try {
                const { data } = await Api.shortFunctionGet()
                const baseList = [
                    { member: userData?.id, function_code: CODE_FAMILIAR, day_count: 3, status: 0, in_type: 0, out_type: 0 },
                    { member: userData?.id, function_code: CODE_ENDING, day_count: 3, status: 1 },
                    { member: userData?.id, function_code: CODE_VOICEMAIL, day_count: 3, status: 0, in_type: 0, out_type: 0 },
                ]
                const list = !data?.data?.length ? baseList : baseList.map((b) => ({ ...b, ...data.data.find((d) => +d.function_code === +b.function_code) }))
                setShortList(list)
                const vm = list.find((s) => +s.function_code === CODE_VOICEMAIL)
                const fam = list.find((s) => +s.function_code === CODE_FAMILIAR)
                if (vm) setVoicemail({ day_count: vm.day_count ?? 3, status: vm.status ?? 0 })
                if (fam) {
                    const decoded = decodeFamiliarDuration(fam)
                    setFamiliar({
                        ...decoded,
                        status: fam.status ?? 0,
                        in_type: fam.in_type ?? 0,
                        out_type: fam.out_type ?? 0,
                    })
                }
            } catch (e) {
                console.log(e)
                toast.error('Failed to load settings')
            } finally {
                setLoading(false)
            }
        })()
    }, [userData?.id])

    const buildPayload = useCallback(
        (type, id, day, status, in_type, out_type) => {
            const fam = shortList.find((s) => +s.function_code === CODE_FAMILIAR)
            const vm = shortList.find((s) => +s.function_code === CODE_VOICEMAIL)
            const end = shortList.find((s) => +s.function_code === CODE_ENDING)
            const famDecoded = fam ? decodeFamiliarDuration(fam) : { durationValue: 3, durationUnit: 'days' }
            const famStored = encodeFamiliarDuration(famDecoded.durationValue, famDecoded.durationUnit)
            return {
                id,
                type,
                member: userData?.id,
                status,
                day,
                in_type: in_type ?? fam?.in_type ?? 0,
                out_type: out_type ?? fam?.out_type ?? 0,
                familiar_client: famStored.day_count,
                ending_call: end?.day_count ?? 3,
                voice_mail: vm?.day_count ?? 3,
                sip_operator_mail: shortList.find((s) => +s.function_code === 100098)?.day_count ?? 3,
            }
        },
        [shortList, userData?.id]
    )

    const handleSave = useCallback(async () => {
        setSaving(true)
        try {
            if (section !== 'familiar') {
                const payloadVm = buildPayload('voice_mail', voicemailRow?.id ?? 0, voicemail.day_count, voicemail.status, 0, 0)
                payloadVm.voice_mail = voicemail.day_count
                await Api.shortFunctionEdit(payloadVm)
            }
            if (section !== 'voicemail') {
                const { day_count, duration_hours } = encodeFamiliarDuration(familiar.durationValue, familiar.durationUnit)
                const payloadFam = buildPayload(
                    'familiar_client',
                    familiarRow?.id ?? 0,
                    day_count,
                    familiar.status,
                    familiar.in_type,
                    familiar.out_type
                )
                payloadFam.day = day_count
                payloadFam.duration_hours = duration_hours
                payloadFam.familiar_client = day_count
                await Api.shortFunctionEdit(payloadFam)
            }
            toast.success(global.success?.[langIndex] || 'Changes saved')
            navigate('/short_function')
        } catch (e) {
            console.log(e)
            toast.error('Failed to save')
        } finally {
            setSaving(false)
        }
    }, [section, voicemail, familiar, voicemailRow?.id, familiarRow?.id, buildPayload, langIndex, navigate])

    const handleDelete = useCallback(async () => {
        const text =
            langIndex === 1 ? 'Are you sure you want to delete this configuration?' :
            langIndex === 2 ? 'Вы уверены, что хотите удалить эту конфигурацию?' :
            'Վստա՞հ եք, որ ցանկանում եք ջնջել այս կարգավորումը։'
        if (!window.confirm(text)) return

        try {
            const isVoice = section === 'voicemail'
            if (isVoice) {
                await Api.shortFunctionDelete(voicemailRow?.id)
            } else {
                await Api.shortFunctionDelete(familiarRow?.id)
            }
            
            toast.success(global.deleted?.[langIndex] || 'Deleted')
            navigate('/short_function')
        } catch (e) {
            console.log(e)
            toast.error('Failed to delete')
        }
    }, [section, voicemailRow?.id, familiarRow?.id, langIndex, navigate])

    if (loading) {
        return (
            <Wrapper>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                    <CircularProgress sx={{ color: primaryColor }} />
                </Box>
            </Wrapper>
        )
    }

    return (
        <Wrapper>
            <div className="tableParent">
                <Paper sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3, borderBottom: '2px solid rgba(0, 0, 0, 0.08)', pb: 2.5 }}>
                        <IconButton size="small" sx={{ color: primaryColor, fontSize: 18, '&:hover': { backgroundColor: `${primaryColor}15` } }} onClick={() => navigate(-1)} aria-label="Back">
                            <ArrowBackIosNewIcon sx={{ fontSize: 20, fontWeight: 700 }} />
                        </IconButton>
                        <Typography variant="h5" sx={{ fontWeight: 700, m: 0 }}>
                            {section === 'voicemail'
                                ? (shortFunctionList['59999']?.[langIndex] || 'Voice mail')
                                : section === 'familiar'
                                    ? (shortFunctionList['10999']?.[langIndex] || 'Familiar employee')
                                    : (shortFunction.config_page?.[langIndex] || 'Config')}
                        </Typography>
                    </Box>

                    <Alert
                        icon={<InfoIcon />}
                        severity="success"
                        sx={
                            REACT_APP_SERVER !== 'ATS'
                                ? { mb: 3 }
                                : {
                                    mb: 3,
                                    border: `1px solid ${primaryColor}`,
                                    backgroundColor: `${primaryColor}10`,
                                    '& .MuiAlert-icon': { color: primaryColor },
                                }
                        }
                    >
                        <Typography variant="body2">
                            {shortFunction.voicemail_familiar_description?.[langIndex] || 'Configure voice mail and familiar employee (call routing) settings.'}
                        </Typography>
                    </Alert>

                    <Paper sx={{ p: 3, border: `1px solid ${primaryColor}` }}>
                        {(!section || section === 'voicemail') && (
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="h6" sx={sectionTitleStyles}>
                                {shortFunctionList['59999']?.[langIndex] || 'Voice mail'}
                            </Typography>
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label={global.day?.[langIndex] || 'Day'}
                                        type="number"
                                        value={voicemail.day_count}
                                        onChange={(e) => {
                                            const val = Math.min(30, Math.max(0, +e.target.value || 0))
                                            setVoicemail((p) => ({ ...p, day_count: val }))
                                        }}
                                        inputProps={{ min: 0, max: 30 }}
                                        variant="outlined"
                                        sx={inputStylesLocal}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={voicemail.status === 1}
                                                onChange={() => setVoicemail((p) => ({ ...p, status: p.status === 1 ? 0 : 1 }))}
                                                sx={{
                                                    '& .MuiSwitch-switchBase.Mui-checked': { color: primaryColor },
                                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: primaryColor },
                                                }}
                                            />
                                        }
                                        label={voicemail.status === 1 ? shortFunction.status?.[langIndex] : shortFunction.in_status?.[langIndex]}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                        )}

                        {(!section || section === 'familiar') && (
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="h6" sx={sectionTitleStyles}>
                                {shortFunctionList['10999']?.[langIndex] || 'Familiar employee'}
                            </Typography>
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        <ToggleButtonGroup
                                            exclusive
                                            size="small"
                                            value={familiar.durationUnit}
                                            onChange={(_, unit) => {
                                                if (unit == null) return
                                                setFamiliar((p) => ({
                                                    ...p,
                                                    durationUnit: unit,
                                                    durationValue: unit === 'hours' ? Math.min(23, Math.max(1, p.durationValue || 1)) : Math.min(30, p.durationValue || 3),
                                                }))
                                            }}
                                            sx={{
                                                '& .MuiToggleButton-root': {
                                                    textTransform: 'none',
                                                    px: 2,
                                                    '&.Mui-selected': { color: primaryColor, backgroundColor: `${primaryColor}15` },
                                                },
                                            }}
                                        >
                                            <ToggleButton value="hours">{global.hour?.[langIndex] || 'Hour'}</ToggleButton>
                                            <ToggleButton value="days">{global.day?.[langIndex] || 'Day'}</ToggleButton>
                                        </ToggleButtonGroup>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label={familiar.durationUnit === 'hours' ? (global.hour?.[langIndex] || 'Hour') : (global.day?.[langIndex] || 'Day')}
                                            type="number"
                                            value={familiar.durationValue}
                                            onChange={(e) => {
                                                const raw = +e.target.value || 0
                                                const val =
                                                    familiar.durationUnit === 'hours'
                                                        ? Math.min(23, Math.max(1, raw || 1))
                                                        : Math.min(30, Math.max(0, raw))
                                                setFamiliar((p) => ({ ...p, durationValue: val }))
                                            }}
                                            inputProps={{
                                                min: familiar.durationUnit === 'hours' ? 1 : 0,
                                                max: familiar.durationUnit === 'hours' ? 23 : 30,
                                            }}
                                            variant="outlined"
                                            sx={inputStylesLocal}
                                        />
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#334155' }}>
                                        Disposition
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                        {[
                                            { value: 0, label: short.in?.[langIndex] },
                                            { value: 1, label: short.out?.[langIndex] },
                                            { value: 2, label: short.both?.[langIndex] },
                                        ].map((opt) => (
                                            <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                                                <input
                                                    type="radio"
                                                    name="disposition"
                                                    checked={familiar.status === opt.value}
                                                    onChange={() => setFamiliar((p) => ({ ...p, status: opt.value }))}
                                                />
                                                <span>{opt.label}</span>
                                            </label>
                                        ))}
                                    </Box>
                                </Grid>
                                {familiar.status === 0 && (
                                    <Grid item xs={12}>
                                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#334155' }}>
                                            {global.only?.[langIndex]} / {global.all?.[langIndex]}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                                                <input type="radio" name="in_only" checked={!familiar.in_type} onChange={() => setFamiliar((p) => ({ ...p, in_type: 0 }))} />
                                                <span>{global.only?.[langIndex]}</span>
                                            </label>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                                                <input type="radio" name="in_only" checked={!!familiar.in_type} onChange={() => setFamiliar((p) => ({ ...p, in_type: 1 }))} />
                                                <span>{global.all?.[langIndex]}</span>
                                            </label>
                                        </Box>
                                    </Grid>
                                )}
                                {familiar.status === 1 && (
                                    <Grid item xs={12}>
                                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#334155' }}>
                                            {global.only?.[langIndex]} / {global.all?.[langIndex]}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                                                <input type="radio" name="out_only" checked={!familiar.out_type} onChange={() => setFamiliar((p) => ({ ...p, out_type: 0 }))} />
                                                <span>{global.only?.[langIndex]}</span>
                                            </label>
                                            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                                                <input type="radio" name="out_only" checked={!!familiar.out_type} onChange={() => setFamiliar((p) => ({ ...p, out_type: 1 }))} />
                                                <span>{global.all?.[langIndex]}</span>
                                            </label>
                                        </Box>
                                    </Grid>
                                )}
                                {familiar.status === 2 && (
                                    <>
                                        <Grid item xs={12}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#334155' }}>
                                                {short.in?.[langIndex]}
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                                                    <input type="radio" name="in_only" checked={!familiar.in_type} onChange={() => setFamiliar((p) => ({ ...p, in_type: 0 }))} />
                                                    <span>{global.only?.[langIndex]}</span>
                                                </label>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                                                    <input type="radio" name="in_only" checked={!!familiar.in_type} onChange={() => setFamiliar((p) => ({ ...p, in_type: 1 }))} />
                                                    <span>{global.all?.[langIndex]}</span>
                                                </label>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#334155' }}>
                                                {short.out?.[langIndex]}
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                                                    <input type="radio" name="out_only" checked={!familiar.out_type} onChange={() => setFamiliar((p) => ({ ...p, out_type: 0 }))} />
                                                    <span>{global.only?.[langIndex]}</span>
                                                </label>
                                                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                                                    <input type="radio" name="out_only" checked={!!familiar.out_type} onChange={() => setFamiliar((p) => ({ ...p, out_type: 1 }))} />
                                                    <span>{global.all?.[langIndex]}</span>
                                                </label>
                                            </Box>
                                        </Grid>
                                    </>
                                )}
                            </Grid>
                        </Box>
                        )}
                    </Paper>

                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 3 }}>
                        {!!section && !!voicemailRow?.id && !!familiarRow?.id && (
                            <Button
                                variant="outlined"
                                onClick={handleDelete}
                                sx={{ textTransform: 'none', color: '#ce405f', borderColor: '#ce405f', '&:hover': { borderColor: '#b62d4a', color: '#b62d4a' } }}
                            >
                                {global.delete?.[langIndex] || 'Delete'}
                            </Button>
                        )}
                        <Button
                            variant="outlined"
                            onClick={() => navigate(-1)}
                            sx={{ textTransform: 'none', color: primaryColor, borderColor: primaryColor, '&:hover': { borderColor: hoverColor, color: hoverColor } }}
                        >
                            {shortFunction.operator_close?.[langIndex] || 'Cancel'}
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSave}
                            disabled={saving}
                            sx={{
                                backgroundColor: primaryColor,
                                color: '#fff',
                                px: 3,
                                py: 0.75,
                                '&:hover': { backgroundColor: hoverColor },
                            }}
                        >
                            {saving ? '...' : (global.edit?.[langIndex] || 'Save')}
                        </Button>
                    </Box>
                </Paper>
            </div>
        </Wrapper>
    )
}
